/**
 * The Billing tab's status-pill chrome and tone vocabulary, composed by both
 * arms (period statuses on the customer side, payment statuses on the expert
 * side) so the two can't drift into different pills for the same idea.
 *
 * Tones are the shared engagement vocabulary (`TONE_CLASSES`), imported rather
 * than re-declared — the `live/StatusBadge.tsx` precedent. Only `failed` is new
 * here: no engagement status is ever red, but a billing period can be.
 *
 * The pill geometry is the HoursTab `StatusPill` language — rounded-full with
 * a leading tone dot — so the in-Hub status idiom stays one thing. The type
 * token is `body-2`: `util-caption` is 10px, which QA read as unreadable
 * across this feature (ENG-5448).
 */
import { TONE_CLASSES } from '../../components/shared/EngagementStatusBadge';

export interface Tone {
  dot: string;
  text: string;
  bg: string;
  border: string;
}

/**
 * Billing's names for the shared tones: brand = money moved, yellow = out of
 * our hands but expected, purple = in progress on our side, grey = terminal
 * bookkeeping, red = needs someone.
 */
export const TONES: Record<'moved' | 'awaiting' | 'working' | 'quiet' | 'failed', Tone> = {
  moved: TONE_CLASSES.active,
  awaiting: TONE_CLASSES.attention,
  working: TONE_CLASSES.progress,
  quiet: TONE_CLASSES.muted,
  failed: {
    dot: 'bg-red-500',
    text: 'text-red-900 dark:text-red-200',
    bg: 'bg-red-50 dark:bg-red-900/15',
    border: 'border-red-700/40',
  },
};

export default function TonePill({
  tone,
  children,
}: {
  tone: Tone;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <span
      className={`inline-flex w-fit items-center gap-6 rounded-full border px-8 py-2 body-2 ${tone.bg} ${tone.text} ${tone.border}`}
    >
      <span className={`h-6 w-6 shrink-0 rounded-full ${tone.dot}`} aria-hidden />
      {children}
    </span>
  );
}
